import { Checkbox, Chip, Select, SelectItem } from '@nextui-org/react';

export interface ListValueSelectOption {
  label: string;
  value: string;
}

export interface ListValueSelectProps {
  id?: string;
  /** Plain strings (label === value) or `{ label, value }` options. */
  options: string[] | ListValueSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  ariaLabel: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
}

function normalizeOptions(options: string[] | ListValueSelectOption[]): ListValueSelectOption[] {
  if (options.length === 0) return [];
  if (typeof options[0] === 'string') {
    return (options as string[]).map((option) => ({ label: option, value: option }));
  }
  return options as ListValueSelectOption[];
}

/** Multi-select with visible checkboxes that renders selected options as chips (horizontal scroll when overflowing). */
export function ListValueSelect({
  id,
  options,
  value,
  onChange,
  placeholder,
  ariaLabel,
  isDisabled,
  isInvalid,
  errorMessage,
}: Readonly<ListValueSelectProps>) {
  const normalizedOptions = normalizeOptions(options);

  return (
    <Select
      id={id}
      size="sm"
      selectionMode="multiple"
      selectedKeys={new Set(value)}
      onSelectionChange={(keys) => onChange(Array.from(keys) as string[])}
      className="w-full"
      classNames={{ value: 'min-w-0' }}
      placeholder={placeholder}
      aria-label={ariaLabel}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      isDisabled={isDisabled}
      renderValue={(items) => (
        <div className="flex flex-nowrap gap-1 py-0.5 overflow-x-auto min-w-0 max-w-full">
          {items.map((item) => (
            <Chip
              key={item.key}
              size="sm"
              variant="flat"
              radius="sm"
              className="shrink-0"
              isDisabled={isDisabled}
              onClose={isDisabled ? undefined : () => onChange(value.filter((v) => v !== String(item.key)))}
            >
              {item.textValue ?? String(item.key)}
            </Chip>
          ))}
        </div>
      )}
    >
      {normalizedOptions.map((option) => (
        <SelectItem
          key={option.value}
          textValue={option.label}
          startContent={
            <Checkbox
              isSelected={value.includes(option.value)}
              size="sm"
              className="pointer-events-none"
              aria-hidden="true"
            />
          }
        >
          {option.label}
        </SelectItem>
      ))}
    </Select>
  );
}

export default ListValueSelect;
